import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface YearlyData {
  month: string;
  monthName: string;
  budgetTotalMonth: number;
  actualAsOfYesterday: number;
}

interface YearlyChartProps {
  data: YearlyData[];
  isLoading: boolean;
}

export const YearlyChart: React.FC<YearlyChartProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Årlig oversikt - Budget vs Actual</h3>
        <div className="flex flex-col items-center justify-center space-y-2 h-64">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
          <p className="text-xs text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Årlig oversikt</h3>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Ingen data tilgjengelig</div>
        </div>
      </div>
    );
  }

  const chartData = {
    labels: data.map(item => item.monthName),
    datasets: [
      {
        label: 'Budget total month',
        data: data.map(item => item.budgetTotalMonth),
        backgroundColor: 'rgba(59, 130, 246, 0.8)', // Blue
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
      {
        label: 'Actual as of yesterday',
        data: data.map(item => item.actualAsOfYesterday),
        backgroundColor: 'rgba(34, 197, 94, 0.8)', // Green
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString('no-NO')} kr`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return value.toLocaleString('no-NO') + ' kr';
          }
        }
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h3 className="text-lg font-semibold mb-4">Årlig oversikt - Budget vs Actual</h3>
      <div className="h-80">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};
